import { ChangeDetectorRef, Component, DoCheck, ElementRef } from '@angular/core';
import { TemplatePortal } from '@angular/cdk/portal';
import { tcsFadeInOutAnimation } from '../../fadeInOut.animation';

export enum LoadingType {
  Circular = 'circular',
  Linear = 'linear',
}

export enum LoadingMode {
  Determinate = 'determinate',
  Indeterminate = 'indeterminate',
}

export enum LoadingStrategy {
  Overlay = 'overlay',
  Replace = 'replace',
}

export enum LoadingStyle {
  FullScreen = 'fullscreen',
  Overlay = 'overlay',
  None = 'none',
}

export const CIRCLE_DIAMETER = 100;

@Component({
  selector: 'tcs-loading',
  styleUrls: ['./loading.component.scss'],
  templateUrl: './loading.component.html',
  animations: [tcsFadeInOutAnimation],
})
export class TcsLoadingComponent implements DoCheck {
  private _mode: LoadingMode = LoadingMode.Indeterminate;
  private _defaultMode: LoadingMode = LoadingMode.Indeterminate;
  private _value = 0;
  private _circleDiameter: number = CIRCLE_DIAMETER;

  /**
   * Flag for animation
   */
  animation = false;

  /**
   * Content injected into loading component.
   */
  content!: TemplatePortal<any>;

  /**
   * Sets mode of [TcsLoadingComponent] to LoadingMode.Determinate or LoadingMode.Indeterminate
   */
  set mode(mode: LoadingMode) {
    this._defaultMode = mode;
  }
  get mode(): LoadingMode {
    return this._mode;
  }

  /**
   * Sets value of [TcsLoadingComponent] if mode is 'LoadingMode.Determinate'
   */
  set value(value: number) {
    this._value = value;
    // Check for changes for `OnPush` change detection
    this._changeDetectorRef.markForCheck();
  }
  get value(): number {
    return this._value;
  }

  style: LoadingStyle = LoadingStyle.None;

  /**
   * height: number
   * Sets height of [TcsLoadingComponent].
   */
  height = 100;

  /**
   * type: LoadingType
   * Sets type of [TcsLoadingComponent] rendered.
   */
  type: LoadingType = LoadingType.Circular;

  /**
   * color: primary' | 'accent' | 'warn'
   * Sets theme color of [TcsLoadingComponent] rendered.
   */
  color: 'primary' | 'accent' | 'warn' = 'primary';

  constructor(private _elementRef: ElementRef, private _changeDetectorRef: ChangeDetectorRef) {}

  ngDoCheck(): void {
    // When overlay is used and the host width has a value greater than 1px
    // set the circle diameter when possible incase the loading component was rendered in a hidden state
    if (this.isOverlay() && this._hostHeight() > 1 && this.animation) {
      this._setCircleDiameter();
      this._changeDetectorRef.markForCheck();
    }
  }

  getHeight(): string | undefined {
    // Ignore height if style is `overlay` or `fullscreen`.
    // Add height if child elements have a height and style is `none`, else return default height.
    if (this.isOverlay() || this.isFullScreen()) {
      return undefined;
    } else {
      return this.height ? `${this.height}px` : '150px';
    }
  }

  getCircleDiameter(): number {
    return this._circleDiameter;
  }

  getCircleStrokeWidth(): number {
    // we calculate the stroke width by setting it as 10% of its diameter
    const strokeWidth: number = this.getCircleDiameter() / 10;
    return Math.abs(strokeWidth);
  }

  isCircular(): boolean {
    return this.type === LoadingType.Circular;
  }

  isLinear(): boolean {
    return this.type === LoadingType.Linear;
  }

  isFullScreen(): boolean {
    return this.style === LoadingStyle.FullScreen;
  }

  isOverlay(): boolean {
    return this.style === LoadingStyle.Overlay;
  }

  /**
   * Starts in animation and returns an observable for completition event.
   */
  show(): void {
    /* need to switch back to the selected mode, so we have saved it in another variable
     *  and then recover it. (issue with protractor)
     */
    this._mode = this._defaultMode;
    // Set values before the animations starts
    this._setCircleDiameter();
    // Check for changes for `OnPush` change detection
    this.animation = true;
    this._changeDetectorRef.markForCheck();
  }

  /**
   * Starts out animation and returns an observable for completition event.
   */
  hide(): void {
    this.animation = false;
    /* need to switch back and forth from determinate/indeterminate so the setInterval()
     * inside mat-progress-spinner stops and protractor doesnt timeout waiting to sync.
     */
    this._mode = LoadingMode.Determinate;
    // Check for changes for `OnPush` change detection
    /* little hack to reset the loader value and animation before removing it from DOM
     * else, the loader will appear with prev value when its registered again
     * and will do an animation going prev value to 0.
     */
    this.value = 0;
    // Check for changes for `OnPush` change detection
    this._changeDetectorRef.markForCheck();
  }

  /**
   * Calculate the proper diameter for the circle and set it
   */
  private _setCircleDiameter(): void {
    // we set a default diameter of 100 since this is the default in material
    let diameter: number = CIRCLE_DIAMETER;
    // if height is provided, then we take that as diameter
    if (this.height) {
      diameter = this.height;
      // else if its not provided, then we take the host height
    } else if (this.height === undefined) {
      diameter = this._hostHeight();
    }
    // if the diameter is over CIRCLE_DIAMETER, we set CIRCLE_DIAMETER
    if (diameter <= CIRCLE_DIAMETER) {
      this._circleDiameter = Math.floor(diameter);
    } else {
      this._circleDiameter = CIRCLE_DIAMETER;
    }
  }

  /**
   * Returns the host height of the loading component
   */
  private _hostHeight(): number {
    if ((this._elementRef.nativeElement as HTMLElement) != null) {
      return (this._elementRef.nativeElement as HTMLElement).getBoundingClientRect().height;
    }
    return 0;
  }
}
